import { useReducer, Reducer } from "react";
import { BatchMessagePreviewState } from "../common/batchNotificationInterface";
import { ILanguage } from "../common/interfaces";
import { SegmentedMessage } from "../common/messageSegmentCalculation";
import Shared, { assertUnreachable } from "../common/shared";
import BatchMessageDetailModel from "../models/BatchMessage/BatchMessageDetailModel";
import { LanguageModel } from "../models/BatchMessage/BatchMessageLanguageDetailModel";
import {
	MessageDetail,
	RecurrenceDetail,
} from "../models/BatchMessage/BatchMessageSchedulingDetailModel";
import GraphemeSplitter from "grapheme-splitter";
import { PhoneNumberDynamicVariablesDetail } from "../models/BatchMessage/BatchMessageGroupDetailModel";
import BatchMessagePreviewModel from "../models/BatchMessage/BatchMessagePreviewModel";

type Action =
	| {
			type: "INITIALIZE_BATCH_MESSAGE";
			payload: { batchNotification: BatchMessageDetailModel | null };
	  }
	| {
			type: "MESSAGE_SELECTION_CHANGE";
			payload: { isChecked: boolean; messageIndex: number };
	  }
	| {
			type: "LANGUAGE_SELECTION_CHANGE";
			payload: {
				values: ILanguage[] | null | undefined;
				messageIndex: number;
				languages: LanguageModel[] | null | undefined;
			};
	  }
	| {
			type: "DATE_TIME_SELECTION_CHANGE";
			payload: {
				value: Date | string;
				messageIndex: number;
			};
	  }
	| {
			type: "TIME_ZONE_SELECTION_CHANGE";
			payload: {
				value: string;
				messageIndex: number;
			};
	  }
	| {
			type: "RECURRENCE_DETAIL_CHANGE";
			payload: {
				isRecurring: boolean;
				recurrenceDetails: RecurrenceDetail | undefined;
				messageIndex: number;
			};
	  }
	| {
			type: "SET_PHONE_NUMBER_LIST";
			payload: { phoneNumber: PhoneNumberDynamicVariablesDetail[] };
	  };

function getLanguageIdByCodeAndId(
	id: string,
	code: string,
	languages: LanguageModel[]
): string {
	const temp = languages.find((x) => x.code === code && x.messageTitle === id);
	if (temp) {
		return temp.id;
	}
	return "";
}

function checkDateTimePickerValidation(dateTime: Date | string): boolean {
	if (!dateTime) {
		return true;
	}
	return dateTime &&
		Shared.isBeforeCurrentDateTime(dateTime as Date, new Date())
		? true
		: false;
}

export function ValidateMessagePreviewDetail(
	batchMessagePreviewDetail: BatchMessagePreviewModel
) {
	let isValid: boolean = true;
	for (
		let index = 0;
		index < batchMessagePreviewDetail.messages.length;
		index++
	) {
		const messageDetail = batchMessagePreviewDetail.messages[index];
		if (
			(!messageDetail.timezone ||
				messageDetail.languageIds.length === 0 ||
				checkDateTimePickerValidation(messageDetail.dateTime)) &&
			messageDetail.isSelected
		) {
			isValid = false;
			break;
		}
	}
	return isValid;
}

export function GetMessagePreviewContent(
	languageList: LanguageModel[],
	isMessageTypeVoice: boolean,
	batchMessageDetail: MessageDetail
) {
	if (languageList.length > 0 && languageList.map((m) => m.message)) {
		const batchMessageDetails: string[] = [];
		let audioDurationLength: number = 0;

		const messageLanguageList: LanguageModel[] = languageList.filter(
			(m: LanguageModel) => batchMessageDetail.languageIds.includes(m.id)
		);
		if (messageLanguageList) {
			messageLanguageList.forEach((lang: LanguageModel) => {
				const index = batchMessageDetail.languageIds.findIndex(
					(c: any) => c === lang.id
				);
				if (index > -1) {
					if (isMessageTypeVoice) {
						audioDurationLength += lang.audioDuration;
					} else {
						batchMessageDetails.push(lang.message);
					}
				}
			});
		}
		return isMessageTypeVoice
			? audioDurationLength
			: batchMessageDetails.join("\n");
	}
	return "";
}

export function GetMessageContentLength(
	messageDetailList: LanguageModel[],
	isMessageTypeVoice: boolean,
	batchMessageDetail: MessageDetail
) {
	const messageContent = GetMessagePreviewContent(
		messageDetailList,
		isMessageTypeVoice,
		batchMessageDetail
	);
	if (!isMessageTypeVoice) {
		const graphemeSplitter = new GraphemeSplitter();
		const segmentedMessage = new SegmentedMessage(
			messageContent,
			"auto",
			graphemeSplitter
		);
		return segmentedMessage.segments.length;
	} else {
		return messageContent;
	}
}

export const useBatchMessageDetailConfiguration = () => {
	return useReducer<Reducer<BatchMessagePreviewState, Action>, undefined>(
		(state, action): BatchMessagePreviewState => {
			//reducer needs to return new state based on previous state and action parsed in
			switch (action.type) {
				case "INITIALIZE_BATCH_MESSAGE":
					const languageList =
						action.payload.batchNotification &&
						action.payload.batchNotification &&
						action.payload.batchNotification.batchMessageLanguageDetail &&
						action.payload.batchNotification.batchMessageLanguageDetail
							.languages
							? action.payload.batchNotification.batchMessageLanguageDetail
									.languages
							: [];
					let languages: LanguageModel[] = JSON.parse(
						JSON.stringify(languageList)
					);
					let messageTitleList: string[] = languages.map((m) => m.messageTitle);
					messageTitleList = messageTitleList.filter(
						(v: string, i: number) => messageTitleList.indexOf(v) === i
					);
					const batchMessagePreviewDetail: BatchMessagePreviewModel = new BatchMessagePreviewModel();
					messageTitleList.forEach((message: string) => {
						const messageDetail: MessageDetail = new MessageDetail();
						messageDetail.title = message;
						messageDetail.dateTime = new Date(
							new Date().setMinutes(new Date().getMinutes() + 15)
						);
						batchMessagePreviewDetail.messages.push(messageDetail);
					});
					batchMessagePreviewDetail.preSubmissionConfigurationId = action.payload.batchNotification?.id!;
					batchMessagePreviewDetail.partitionKey = action.payload.batchNotification?.partitionKey!;
					return {
						...state,
						batchMessagePreviewDetail: batchMessagePreviewDetail,
					};
				case "MESSAGE_SELECTION_CHANGE":
					const batchMessageDetail = state.batchMessagePreviewDetail;
					batchMessageDetail.messages[action.payload.messageIndex].isSelected =
						action.payload.isChecked;
					return {
						...state,
						batchMessagePreviewDetail: batchMessageDetail,
					};
				case "LANGUAGE_SELECTION_CHANGE":
					const batchMessageDetailForLanguage = state.batchMessagePreviewDetail;
					const languageListId: string[] = [];

					if (action.payload.values && action.payload.values.length > 0) {
						action.payload.values.forEach((element: ILanguage) => {
							languageListId.push(
								getLanguageIdByCodeAndId(
									batchMessageDetailForLanguage.messages[
										action.payload.messageIndex
									].title,
									element.code,
									action.payload.languages &&
										action.payload.languages.length > 0
										? action.payload.languages
										: []
								)
							);
						});
					}
					batchMessageDetailForLanguage.messages[
						action.payload.messageIndex
					].languageIds = languageListId;
					return {
						...state,
						batchMessagePreviewDetail: batchMessageDetailForLanguage,
					};
				case "DATE_TIME_SELECTION_CHANGE":
					const batchMessageSchedulingDetailT = state.batchMessagePreviewDetail;

					batchMessageSchedulingDetailT.messages[
						action.payload.messageIndex
					].dateTime = action.payload.value
						? new Date(action.payload.value)
						: "";
					return {
						...state,
						batchMessagePreviewDetail: batchMessageSchedulingDetailT,
					};
				case "TIME_ZONE_SELECTION_CHANGE":
					const batchMessageSchedulingDetailsTempTwo =
						state.batchMessagePreviewDetail;
					batchMessageSchedulingDetailsTempTwo.messages[
						action.payload.messageIndex
					].timezone = action.payload.value;

					return {
						...state,
						batchMessagePreviewDetail: batchMessageSchedulingDetailsTempTwo,
					};
				case "RECURRENCE_DETAIL_CHANGE":
					const batchMessageSchedulingDetailsTempThree =
						state.batchMessagePreviewDetail;
					batchMessageSchedulingDetailsTempThree.messages[
						action.payload.messageIndex
					].isRecurring = action.payload.isRecurring;
					batchMessageSchedulingDetailsTempThree.messages[
						action.payload.messageIndex
					].recurrenceDetails = action.payload.recurrenceDetails;

					return {
						...state,
						batchMessagePreviewDetail: batchMessageSchedulingDetailsTempThree,
					};
				case "SET_PHONE_NUMBER_LIST":
					const batchMessageDetailForPhoneNumber =
						state.batchMessagePreviewDetail;
					batchMessageDetailForPhoneNumber.phoneNumbers =
						action.payload.phoneNumber;
					return {
						...state,
						batchMessagePreviewDetail: batchMessageDetailForPhoneNumber,
					};
			}
			assertUnreachable(action);
		},
		undefined,
		() => ({
			batchMessagePreviewDetail: new BatchMessagePreviewModel(),
		})
	);
};
